(ns app.front.file.upload
  ;; cljfmt and kondo disagree about sorting uppercase
  {:clj-kondo/config {:linters {:unsorted-required-namespaces {:level :off}}}}
  #_{:clj-kondo/ignore [:refer]}
  (:require ["mime/lite" :refer [Mime]]
            ["mime/types/other.js" :default other-types]
            ["mime/types/standard.js" :default standard-types]
            [ajax.core :refer [PUT] :as ajax]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [goog.crypt.base64 :as base64]
            [goog.crypt.Md5]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; Add EDN to the file extensions recognized by the mime library
(defonce mime (doto (Mime. standard-types other-types)
                (.define (clj->js {"application/edn" ["edn"]}) true)))

(def ignore-files
  "indexing files"
  #{".DS_Store" "Thumbs.db"})


(defn- mime-type-by-extension [name]
  (->> (str/split name #"\.")
       last
       (.getType ^js mime)))


(defn file-type [file]
  (or (str-util/blank->nil (.-type file))
      (mime-type-by-extension (.-name file))))


(rf/reg-event-fx ::start-file-upload
  (fn [{:keys [db]} [_ file md5-base64 attach-file-fn component-id fullpath on-error _ {:keys [file-id upload-url]}]]
    (let [fullpath       (or (str-util/blank->nil (.-webkitRelativePath file))
                             fullpath
                             (.-name file))
          file-mime-type (file-type file)
          filename       (.-name file)]
      {:db (update db
                   ::pending-file-requests
                   conj
                   (PUT upload-url
                     {:headers       {"Content-Type" file-mime-type}
                      :body          file
                      :handler       (fn [_]
                                       (attach-file-fn
                                         (util/mmap filename file-id file-mime-type md5-base64 fullpath))
                                       (>evt [::remove-pending-file component-id :placeholder])
                                       (>evt [::remove-pending-file component-id md5-base64]))
                      :error-handler (fn [{:keys [response status]}]
                                       ;; no notifications when canceled by client
                                       (when (not= status -1)
                                         (>evt [::upload-failure
                                                (util/mmap {:md5 md5-base64} component-id on-error)
                                                status
                                                response])))}))})))


(rf/reg-event-db ::cancel-upload
  (fn [db _]
    (doseq [req (::pending-file-requests db)]
      (ajax/abort req))
    (-> db
        (assoc ::pending-file-requests [])
        (dissoc ::pending-files))))


(rf/reg-event-fx ::file-already-uploaded
  (fn [_ [_ tr file md5-base64 component-id on-error]]
    {:fx [[:dispatch [:notifications/add :toast :success :success (tr [:upload/already-uploaded] [(.-name file)])]]
          [:dispatch [::remove-pending-file component-id :placeholder]]
          [:dispatch [::remove-pending-file component-id md5-base64]]
          (when on-error
            [:dispatch [on-error md5-base64]])]}))


(rf/reg-event-db ::init-upload
  (fn [db _]
    (assoc db ::pending-file-requests [])))


(rf/reg-event-db ::add-pending-file
  (fn [db [_ component-id md5]]
    (update-in db [::pending-files component-id] set/union #{md5})))


(rf/reg-event-db ::remove-pending-file
  (fn [db [_ component-id md5]]
    (update-in db [::pending-files component-id] disj md5)))


(rf/reg-event-fx ::upload-failure
  (fn [_ [_ {:keys [component-id md5 on-error]} _status response]]
    {:fx [[:dispatch [:notifications/add :toast :error :error/file-upload-title (or (:error response)
                                                                                    :error/file-upload-caption)]]
          [:dispatch [::remove-pending-file component-id md5]]
          (when on-error
            [:dispatch [on-error md5]])]}))


(rf/reg-event-fx ::unsupported-file-type-error
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::pending-files)
     :dispatch [:notifications/add :toast :error :error/file-upload-title :upload/unsupported-file-type-error]}))


(rf/reg-sub ::pending-files
  (fn [db [_ component-id]]
    (get-in db [::pending-files component-id])))


(defn open-filepicker [element e]
  (.preventDefault e)
  (-> (.getElementById js/document element)
      .click))


(defn md5 [bytes]
  (-> (doto (goog.crypt.Md5.)
        (.update bytes))
      .digest
      base64/encodeByteArray))

(defn process-file-upload [tr attach-file-fn {:keys [file component-id file-hash fullpath]}]
  (>evt [::add-pending-file component-id file-hash])
  (comm/send-command-execution
    {:command         :attachment/initialize-upload
     :data            {:content-type (file-type file)
                       :md5-digest   file-hash}
     :on-success      [::start-file-upload file file-hash attach-file-fn component-id fullpath nil]
     :on-not-modified [::file-already-uploaded tr file file-hash component-id]
     :on-bad-request  [::upload-failure (util/mmap {:md5 file-hash} component-id)]
     :on-error        [::upload-failure (util/mmap {:md5 file-hash} component-id)]
     :on-server-error [::upload-failure (util/mmap {:md5 file-hash} component-id)]}))

(defn read-file-sizes [file size-constraints]
  (js/Promise.
    (fn [resolve reject]
      (if (and (:bytes size-constraints)
               (= 1 (count size-constraints)))
        (resolve {:bytes (.-size file)})
        (let [reader (js/FileReader.)]
          (set! (.-onload reader)
                (fn [r]
                  (let [img (js/Image.)]
                    (set! (.-src img) (-> r .-target .-result))
                    (-> (.decode img)
                        (.then (fn [_]
                                 (resolve {:width     (.-width img)
                                           :height    (.-height img)
                                           :bytes     (.-size file)
                                           :ratio-w-h (/ (.-width img) (.-height img))
                                           :ratio-h-w (/ (.-height img) (.-width img))})))
                        (.catch (fn [e]
                                  ;; We end up here if the browser cannot decode the file as an image (e.g. HEIC)
                                  (js/console.error e)
                                  (reject e)))))))
          (.readAsDataURL reader file))))))

(defn read-file [{:keys [file component-id on-select-fn full-path]}]
  (let [reader (js/FileReader.)]
    (set! (.-onload reader)
          #(on-select-fn (-> (util/mmap {:file-hash (-> % .-target .-result js/Uint8Array. md5)}
                                        file component-id)
                             (p/assoc-if :fullpath (some-> full-path
                                                           str/trim
                                                           str-util/blank->nil
                                                           (str/replace #"^/" ""))))))
    (.readAsArrayBuffer reader file)))

(defn validate-size [file-size size-constraints]
  (js/Promise.
    (fn [resolve reject]
      (let [errs (->> [:bytes :width :height :ratio-w-h :ratio-h-w]

                      (filter #(and (% size-constraints)
                                    (> (% file-size) (% size-constraints)))))]
        (doseq [err errs]
          (>evt [:notifications/add
                 :toast
                 :error
                 :error/invalid-file-size
                 (keyword "upload" (str "invalid-file-size-" (name err)))]))
        (if (seq errs)
          (reject errs)
          (resolve))))))

(defn check-and-read-file [{:keys [file component-id size-constraints] :as args}]
  (if size-constraints
    (-> (read-file-sizes file size-constraints)
        (.then (fn [file-size]
                 (validate-size file-size size-constraints)))
        (.then (fn [_]
                 (read-file args)))
        (.catch (fn [_]
                  (>evt [::remove-pending-file component-id :placeholder]))))
    (js/Promise.resolve (read-file args))))

(defn- mime-parts [mimetype]
  (str/split mimetype #"/"))

(defn accept-matches [accept-types proposed-type]
  (or (accept-types proposed-type)
      (contains? (->> accept-types
                      (map mime-parts)
                      (filter #(-> % second (= "*")))
                      (map first)
                      set)
                 (-> proposed-type mime-parts first))))

(defn extension-match-only? [accept-types]
  (every? #(re-matches #"^\..+$" %) accept-types))

(defn file-is-accepted-type-fn [accept-types]
  (let [ext-match? (extension-match-only? accept-types)]
    (fn [^js file]
      (and (not (ignore-files (.-name file)))
           (or (empty? accept-types)
               (and ext-match?
                    (some #(str/ends-with? (.-name file) %) accept-types))
               (accept-matches accept-types (file-type file)))))))

(defn read-files-and-entries
  "files-or-entries contains a sequence of File or FileSystemEntry objects"
  [{:keys [files-or-entries on-select-fn on-processing-files component-id accept-types size-constraints]}]
  (when (seq files-or-entries)
    (try
      (if-let [accepted-items (->> files-or-entries
                                   (filter (file-is-accepted-type-fn accept-types))
                                   seq)]
        (do
          (>evt [::add-pending-file component-id :placeholder])
          (when on-processing-files
            (on-processing-files accepted-items))
          (doseq [file-or-entry accepted-items]
            (if (instance? js/File file-or-entry)
              (check-and-read-file (util/mmap {:file      file-or-entry
                                               :full-path (.-webkitRelativePath file-or-entry)}
                                              component-id on-select-fn size-constraints))
              (.file file-or-entry
                     (fn [^File file]
                       (check-and-read-file (util/mmap {:file      file
                                                        :full-path (or (.-fullPath file-or-entry)
                                                                       (.-webkitRelativePath file))}
                                                       component-id on-select-fn size-constraints)))))))
        (do (js/console.error "No acceptable file types in" (clj->js files-or-entries))
            (>evt [:notifications/add :toast :error :error/invalid-file-type :upload/unsupported-file-type-error])))
      (catch js/Error e
        (.log js/console "Unable to read files: " (str e))))))


(defn handle-file-upload
  [{:keys [file-input-id on-select-fn component-id accept-types on-processing-files size-constraints]}]
  (p/if-all-let [elem  (.getElementById js/document file-input-id)
                 files (->> elem .-files array-seq)]
    (do
      (read-files-and-entries (util/mmap {:files-or-entries files}
                                         on-select-fn on-processing-files component-id accept-types size-constraints))
      ;; Have to reset the input value so that next change is detected properly
      (set! (.-value elem) ""))))


(defn dragging-files? [select-directories? accept-types e]
  (->> e
       .-dataTransfer
       .-items
       array-seq
       ;; item data is unavailable until the actual drop
       ;; hence the types of the files under the directory cannot be verified until then
       (every? #(or (and select-directories? (= (.-kind %) "file"))
                    (accept-types (file-type %))
                    (empty? accept-types)))))


(defn file-picker-button
  "Creates a button for handling a user uploaded file without necessarily uploading it to the server.
  Props:
  `id`             component id
  `tr`             localization function
  `on-select-fn`   called when the user selects a file, called with three arguments:
                      file           for doing whatever it is you want to do
                      component id   for updating the pending status of the file picker
                      file hash      for identifying the file
                    note that you will have to mark the file processing as complete after this is done
  `accept-types`    a seq of string file types formats, e.g. [\".pdf\"]
  `multiple`        true if multiple files can be chosen
  `class`           CSS classes given to the button container
  `button-opts`     options map passed on to the `cc/button`
  `size-constraints` constraints that the selected file must fulfill, a map containing any of these keys:
                       :width     maximum pixel width of the image (int)
                       :height    maximum pixel height of the image (int)
                       :bytes     maximum byte size of the image file (int)
                       :ratio-w-h maximum allowed ratio of (/ width height) (decimal)
                       :ratio-h-w maximum allowed ratio of (/ height width) (decimal)"
  [{:keys [id tr on-select-fn class accept-types multiple button-opts size-constraints]} & button-contents]
  (r/with-let [*show-loading? (r/atom false)]
    (let [file-input-id (str id "-file-input")
          button-id     (str id "-button")
          pending-files (<sub [::pending-files id])]
      [:div.upload-button {:class class}
       (if (or @*show-loading? (seq pending-files))
         (cc/inline-loading {:description (tr [:file/uploading])
                             :success     (empty? pending-files)
                             :on-success  (fn [_]
                                            (reset! *show-loading? false)
                                            true)})
         [:<>
          [:input (p/assoc-if {:type            "file"
                               :id              file-input-id
                               :style           {:display "none"}
                               :on-change       (fn [_]
                                                  (reset! *show-loading? true)
                                                  (handle-file-upload (util/mmap {:accept-types (set accept-types)
                                                                                  :component-id id}
                                                                                 file-input-id
                                                                                 on-select-fn
                                                                                 size-constraints)))
                               :multiple        multiple
                               :aria-labelledby button-id}
                              :accept (str/join "," accept-types))]
          (cc/button
            (assoc button-opts :on-click #(open-filepicker file-input-id %) :id button-id)
            button-contents)])])))

(defn make-attach-file-handler
  "Make a modern attach-file handler from old or use new style"
  [attach-file-fn attach-file-v2-fn]
  (or attach-file-v2-fn
      (fn [{:keys [file-id filename file-mime-type md5-base64 _fullpath]}]
        (attach-file-fn file-id filename file-mime-type md5-base64))))

(defn upload-button
  "Creates a button for uploading a generic file.
  `attach-file-fn` will be called with four arguments: file-id filename file-mime-type md5-base64
  `attach-file-v2-fn` is a modern alternative that takes a map with the same + :fullpath"
  [id
   {:keys [attach-file-fn
           attach-file-v2-fn
           button-i18n-key
           disabled?
           accept-types
           multiple
           class
           button-opts
           label-text
           size-constraints]}]
  (let [tr         (<sub [:tr])
        label-text (if label-text
                     label-text
                     (tr [(or button-i18n-key :file/upload)]))]
    [file-picker-button
     (util/mmap {:tr           tr
                 :button-opts  (merge {:kind             :ghost
                                       :icon             i/plus
                                       :icon-description ""
                                       :disabled         (boolean disabled?)}
                                      button-opts)
                 :on-select-fn (partial process-file-upload
                                        tr
                                        (make-attach-file-handler attach-file-fn attach-file-v2-fn))}
                id accept-types multiple class size-constraints)
     label-text]))


(defn stop-event [e]
  (.stopPropagation e)
  (.preventDefault e))


(defn drag-over [e disabled?]
  (stop-event e)
  (-> e .-dataTransfer .-dropEffect (set! (if disabled? "none" "copy"))))


(defn handle-entry [^js file-system-entry]
  (js/Promise. (fn [resolve _]
                 (if (.-isFile file-system-entry)
                   (resolve file-system-entry)
                   (-> (.createReader file-system-entry)
                       (.readEntries #(->> (map handle-entry %)
                                           js/Promise.all
                                           resolve)))))))


(defn read-files-and-directories [dt *all-entries]
  (let [items (array-seq (.-items dt))]
    (js/Promise.
      (fn [resolve _]
        (let [*iteration-attempts (r/atom [])
              handle-items        (fn handle-items [items]
                                    (if (seq items)
                                      (let [item  (first items)
                                            entry (.webkitGetAsEntry item)]
                                        (swap! *iteration-attempts conj
                                               (if (.-isDirectory entry)
                                                 (-> (handle-entry entry)
                                                     (.then (fn [entries]
                                                              (->> (.flat entries js/Infinity)
                                                                   (swap! *all-entries concat))))
                                                     (.catch (fn [err]
                                                               (.log js/console "Error occurred when traversing directory: " (str err)))))
                                                 (if (.-isFile entry)
                                                   (swap! *all-entries conj entry)
                                                   (>evt [::unsupported-file-type-error]))))
                                        (handle-items (next items)))
                                      (resolve (js/Promise.all @*iteration-attempts))))]
          (handle-items items))))))

(defn- handle-file-drop-fn
  "produce event-handler for the onDrop event"
  [*all-entries *show-loading select-directories? id accept-types on-select-fn on-processing-files]
  (fn [e]
    (stop-event e)
    (>evt [::init-upload])
    (reset! *all-entries [])
    (when-let [dt (.-dataTransfer e)]
      (let [files (-> (.-files dt)
                      array-seq)]
        (reset! *show-loading true)
        (if select-directories?
          (-> (read-files-and-directories dt *all-entries)
              (.then (fn [_]
                       (read-files-and-entries (util/mmap {:files-or-entries @*all-entries
                                                           :component-id     id
                                                           :accept-types     (set accept-types)}
                                                          on-select-fn on-processing-files))))
              (.catch (fn [err]
                        (.log js/console "Error occurred when traversing directory: " (str err)))))
          (read-files-and-entries (util/mmap {:files-or-entries files
                                              :component-id     id
                                              :accept-types     (set accept-types)}
                                             on-select-fn on-processing-files)))))))

(defn upload-area
  "Creates an upload area box like on the attachments tabs. It can be used as a drag-and-drop target or via
   clicking a link inside the box.

   Arguments:
   `id`: unique id for the component
   `opts`: {:attach-file-fn      : handler function called like `(attach-file-fn file-id filename file-mime-type md5-base64)`
                                   after successful upload and file entity creation
            :attach-file-v2-fn   : handler function called like `[{:keys [file-id filename file-mime-type md5-base64 fullpath]}]`
            :accept-types        : list of file (mime) types as accepted by <input type=file> accept attribute.
            :on-processing-files : handler function called like `(on-processing-files files)` before starting to initialize upload for files
            :select-directories?    : if true, user has to select/drop directories instead of individual files.
            :process-file-upload-fn : optional replacement to the default `process-file-upload`, usually not needed
            :disabled?              : when true, prevents selecting or dropping files"
  [id {:keys [attach-file-fn
              attach-file-v2-fn
              accept-types
              additional-help
              hide-styles
              additional-content
              process-file-upload-fn
              on-processing-files
              select-directories?
              disabled?]
       :or   {additional-help :attachments/upload-text3}}]
  (r/with-let [*show-loading (r/atom false)
               *all-entries  (r/atom [])]
    (let [file-input-id    (str id "-file-input-area")
          pending-files    (<sub [::pending-files id])
          tr               (<sub [:tr])
          on-select-fn     (partial (or process-file-upload-fn process-file-upload)
                                    tr
                                    (make-attach-file-handler attach-file-fn attach-file-v2-fn))
          handle-file-drop (handle-file-drop-fn *all-entries
                                                *show-loading
                                                select-directories?
                                                id
                                                accept-types
                                                on-select-fn
                                                on-processing-files)
          dragging-files?  (partial dragging-files? select-directories? (set accept-types))]
      [:div
       {:class (when (not hide-styles) "upload-area")}
       [:div
        {:class         (when (not hide-styles) "upload-area__drop-area")
         :on-drag-enter (fn [e]
                          (when (dragging-files? e)
                            (stop-event e)))
         :on-drag-over  (fn [e]
                          (when (dragging-files? e)
                            (drag-over e disabled?)))
         :on-drop       (fn [e]
                          (when (dragging-files? e)
                            (if disabled?
                              (stop-event e)
                              (handle-file-drop e))))}
        (if (and (or @*show-loading (seq pending-files))
                 (not hide-styles))
          [:div.upload-area__loading
           (cc/inline-loading {:description (tr [:file/uploading])
                               :success     (empty? pending-files)
                               :on-success  (fn [_]
                                              (js/setTimeout #(reset! *show-loading false) 1000)
                                              true)})]
          [:<>
           (when (not hide-styles)
             [:div
              [:input (p/assoc-if {:type      "file"
                                   :id        file-input-id
                                   :multiple  "multiple"
                                   :style     {:display "none"}
                                   :disabled  disabled?
                                   :on-change (fn [_]
                                                (when-not disabled?
                                                  (reset! *show-loading true)
                                                  (>evt [::init-upload])
                                                  (handle-file-upload (util/mmap {:accept-types (set accept-types)
                                                                                  :component-id id}
                                                                                 file-input-id
                                                                                 on-select-fn
                                                                                 on-processing-files))))}
                                  :accept (str/join "," accept-types)
                                  :webkitdirectory (when select-directories?
                                                     "webkitdirectory"))]
              [:div.upload-area__drop-area__upper
               [:span (tr [(if select-directories?
                             :attachments/upload-text1-w-directory
                             :attachments/upload-text1)])]
               [:br]
               [:span [:a.bx--link {:href          "#"
                                    :aria-disabled (boolean disabled?)
                                    :on-click      (fn [e]
                                                     (if disabled?
                                                       (stop-event e)
                                                       (open-filepicker file-input-id e)))}
                       (tr [:attachments/upload-link])] " " (tr [:attachments/upload-text2])]]
              [:div.upload-area__drop-area__lower
               [:span (tr [additional-help])]]])
           additional-content])]])))


(rf/reg-event-db ::add-pending-workspace-file
  (fn [db [_ file-id]]
    (update db ::pending-workspace-files set/union #{file-id})))


(rf/reg-event-db ::remove-pending-workspace-file
  (fn [db [_ file-id]]
    (update db ::pending-workspace-files disj file-id)))


(defn add-workspace-file [fsm-target on-success-evt file-id filename content-type md5]
  (>evt [::add-pending-workspace-file file-id])
  (comm/send-command-execution
    {:command          :file/add-workspace-file
     :data             {:workspace-id      (-> fsm-target :entity/parent :workspace/id)
                        :file-id           file-id
                        :file/filename     filename
                        :file/content-type content-type
                        :file/category     :file.category/public-notice-document
                        :file/md5          md5
                        :convert-to-pdfa?  true}
     :on-success       on-success-evt
     :finally-dispatch [::remove-pending-workspace-file file-id]}))


(rf/reg-sub ::workspace-files-pending?
  #(-> % ::pending-workspace-files seq))
